<script setup lang="ts">
// BGA/JH step-aside anchor tile (2026-09-21 durability arc — V107).
//
// Surfaces the durable prior US-close reference (nq_close_snapshot) that
// signal_core.engine.step_aside_prev_close depends on. The failure story
// this tile makes visible: over the 2026-09-20/21 weekend the anchor
// went None across restarts and nothing on the dashboard showed it — one
// WARN per day per fund and that was the only signal. This tile turns
// red when the same-shape failure recurs.
//
// State semantics (server-classified in HealthMonitorService
// ClassifyNqCloseSnapshotState, so tile and WhatsApp alert agree):
//   fresh   — anchor < 48h old (half of the 96h alarm threshold)
//   aging   — 48-96h old; normal weekend (Fri close → Mon check ~58h)
//   stale   — > 96h old; health_alert already fired
//   missing — snapshot table empty; hydrate cascade will fail cold-start
//
// 2026-09-22 (paolo): styled to match the K_t pane's tile family and
// gained an (i) legend so a first-time reader can tell what the number
// is, where it comes from and when it stops being trustworthy.
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { api, type StepAsideAnchorState } from '../api/client'

const state = ref<StepAsideAnchorState | null>(null)
const error = ref<string | null>(null)
const legendOpen = ref(false)
let timer: ReturnType<typeof setInterval> | null = null

async function fetchState() {
  try {
    state.value = await api.getStepAsideAnchorState()
    error.value = null
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to load anchor state'
  }
}

const tileClass = computed(() => {
  const s = state.value?.state
  // Reuse the pane's tile-family classes so the anchor sits inside the
  // same visual vocabulary as the K_t magnitude tiles above. Aliases
  // (anchor-tile--*) are preserved for the existing Vitest assertions.
  if (s === 'stale' || s === 'missing') return 'tile anchor-tile anchor-tile--fail tile--fail'
  if (s === 'aging') return 'tile anchor-tile anchor-tile--warn tile--warn'
  if (s === 'fresh') return 'tile anchor-tile anchor-tile--ok tile--pass'
  return 'tile anchor-tile'
})

const ageDisplay = computed(() => {
  const h = state.value?.ageHours
  if (h === null || h === undefined) return '—'
  if (h < 1) return `${Math.round(h * 60)}m`
  if (h < 48) return `${h.toFixed(1)}h`
  return `${(h / 24).toFixed(1)}d`
})

const closeTargetDisplay = computed(() => {
  const iso = state.value?.latest?.closeTargetUtc
  if (!iso) return '—'
  return iso.replace('T', ' ').replace(/\..+$/, 'Z')
})

const stateLabel = computed(() => {
  const s = state.value?.state
  switch (s) {
    case 'fresh':   return 'FRESH'
    case 'aging':   return 'AGING'
    case 'stale':   return 'STALE'
    case 'missing': return 'MISSING'
    default:        return '…'
  }
})

// Real-time inputs to the (i) legend so the copy explains THIS anchor,
// not the abstract concept only. Everything renders '—' when the payload
// hasn't arrived yet so the panel isn't blank while it's loading.
const closeValueDisplay = computed(() => {
  const v = state.value?.latest?.closeValue
  return v === undefined || v === null ? '—' : v.toFixed(2)
})
const sourceDisplay = computed(() => state.value?.latest?.source ?? '—')
const latchedAtDisplay = computed(() => {
  const iso = state.value?.latest?.latchedAtUtc
  if (!iso) return '—'
  return iso.replace('T', ' ').replace(/\..+$/, 'Z')
})
const barTsDisplay = computed(() => {
  const iso = state.value?.latest?.barTsUtc
  if (!iso) return '—'
  return iso.replace('T', ' ').replace(/\..+$/, 'Z')
})
const thresholdDisplay = computed(() => {
  const h = state.value?.thresholdHours
  return h === undefined || h === null ? '96h' : `${h}h`
})

onMounted(() => {
  fetchState()
  timer = setInterval(fetchState, 60_000)
})
onUnmounted(() => {
  if (timer !== null) clearInterval(timer)
})
</script>

<template>
  <div :class="tileClass" data-testid="step-aside-anchor-tile">
    <div class="tile-label anchor-tile__header">
      <span class="anchor-tile__title">BGA/JH step-aside anchor</span>
      <div class="anchor-tile__actions">
        <span class="anchor-tile__state" data-testid="anchor-state">{{ stateLabel }}</span>
        <button
          type="button"
          class="kt-info-btn kt-info-btn--tile"
          :aria-expanded="legendOpen"
          aria-controls="anchor-tile-legend"
          :aria-label="legendOpen ? 'Hide anchor explanation' : 'Show anchor explanation'"
          data-testid="anchor-info-toggle"
          @click="legendOpen = !legendOpen"
        >i</button>
      </div>
    </div>
    <div v-if="error" class="anchor-tile__error" data-testid="anchor-error">
      {{ error }}
    </div>
    <template v-else>
      <div class="tile-row">
        <span class="tile-metric-label">Prior US close</span>
        <span class="tile-metric-value" data-testid="anchor-close-target">{{ closeTargetDisplay }}</span>
      </div>
      <div class="tile-row">
        <span class="tile-metric-label">Value</span>
        <span class="tile-metric-value" data-testid="anchor-close-value">{{ closeValueDisplay }}</span>
      </div>
      <div class="tile-row">
        <span class="tile-metric-label">Age</span>
        <span class="tile-metric-value" data-testid="anchor-age">{{ ageDisplay }}</span>
      </div>
      <div class="tile-row">
        <span class="tile-metric-label">Source</span>
        <span class="tile-metric-value" data-testid="anchor-source">{{ sourceDisplay }}</span>
      </div>
    </template>

    <section
      v-if="legendOpen"
      id="anchor-tile-legend"
      class="kt-legend anchor-tile__legend"
      role="region"
      aria-label="Step-aside anchor explanation"
      data-testid="anchor-legend"
    >
      <p class="kt-legend-lead">
        The <b>prior US close</b> of the Nasdaq-100 future — the reference the BGA and JH
        <b>step-aside</b> test compares this morning's live NQ against. If it's stale, the
        gate silently falls back to a wrong anchor and the day's step-aside call cannot
        be trusted. Weekend gap (2026-09-20/21) burned this before we surfaced it here.
      </p>

      <dl class="kt-legend-list">
        <dt>Right now</dt>
        <dd>
          Anchor value <code>{{ closeValueDisplay }}</code>
          from the <code>{{ closeTargetDisplay }}</code> print
          (bar <code>{{ barTsDisplay }}</code>, latched at <code>{{ latchedAtDisplay }}</code>),
          served by <code>{{ sourceDisplay }}</code>. Age: <b>{{ ageDisplay }}</b>.
        </dd>

        <dt>What the label means</dt>
        <dd>
          <b>FRESH</b> — under 48 h old, ready to use.
          <b>AGING</b> — 48–96 h; normal on a Monday after a Friday close.
          <b>STALE</b> — over {{ thresholdDisplay }}; a <code>health_alert</code> has already fired.
          <b>MISSING</b> — the <code>nq_close_snapshot</code> table is empty, so the hydrate
          cascade will fail cold-start.
        </dd>

        <dt>Where it comes from</dt>
        <dd>
          <code>HydratePriorUsClose</code> falls through, in order:
          <code>nq_close_snapshot</code> → <code>nas100_1m</code> → Postgres →
          Databento. The <b>Source</b> row above is the rung that answered. The daily
          <code>backfill_nq_close_snapshot.py</code> keeps the top rung populated.
        </dd>

        <dt>Why it matters here</dt>
        <dd>
          The step-aside decision is what says "sit today out". The
          K_t magnitude tiles above measure BGA/JH tracking quality relative to a
          decision the engine already made using <b>this</b> anchor. Red anchor →
          the tiles' passing corr/RMSE isn't evidence of a good day, just a good fit
          to a bad reference.
        </dd>

        <dt>How often it refreshes</dt>
        <dd>
          This tile re-fetches every <b>60 s</b>. The server-side classification is the
          same one <code>HealthMonitorService</code> uses to raise
          <code>nq_prev_close_stale</code>/<code>_missing</code>, so this tile and the
          WhatsApp alert never disagree.
        </dd>
      </dl>
    </section>
  </div>
</template>

<style scoped>
/* Header row mirrors .tile-label with the state chip + (i) button on the right. */
.anchor-tile__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.15rem;
}
.anchor-tile__title { text-transform: none; letter-spacing: normal; color: #ccc; }
.anchor-tile__actions { display: inline-flex; align-items: center; gap: 0.4rem; }
.anchor-tile__state {
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  padding: 2px 6px;
  border-radius: 3px;
  background: #ffffff10;
  color: #aaa;
}
.anchor-tile--ok   .anchor-tile__state { color: #4caf50; background: #4caf5019; }
.anchor-tile--warn .anchor-tile__state { color: #fbc02d; background: #fbc02d19; }
.anchor-tile--fail .anchor-tile__state { color: #f44336; background: #f4433622; }

.anchor-tile__error { color: #f44336; font-size: 0.8rem; margin-top: 0.15rem; }
.anchor-tile__legend { margin-top: 0.55rem; }
</style>
